import React from 'react';
import classNames from 'classnames';
import { useApolloClient } from '@apollo/client';

import styles from './ideaInformationStyles.scss';

import IdeaInformationLoading from '@buyside/components/presentation/IdeaShowPage/RightSidebar/IdeaInformation/ideaInformationLoading';
import InfoTable from '@buyside/components/presentation/IdeaShowPage/InfoTable/infoTable';
import Link from '@buyside/components/presentation/Link/link';
import TooltipWhite from '@buyside/components/presentation/Tooltips/TooltipWhite/tooltipWhite';

import { entityUrl } from '@buyside/util/urlHelper';

import { MentionedEntity } from '@buyside/components/presentation/EntityMention/entityMentionAutocomplete';
import { Maybe, IdeaCoreQuery } from '@interfaces/graphql-typings';

const IdeaCore = require('buyside/graphql/queries/ideaShowPage/IdeaCore');

const catalystItems = ([primaryCatalyst, ...rest]: string[]): React.ReactElement => (
  <div>
    {primaryCatalyst || 'N/A'}
    {rest.length > 0 && (
      <div>
        and
        <TooltipWhite
          overlay={
            <div>
              {rest.map(
                (catalyst): React.ReactElement => (
                  <div key={catalyst}>{catalyst}</div>
                )
              )}
            </div>
          }
          trigger={['click']}
          placement="bottom"
        >
          <span className={styles.moreText}> {rest.length} more</span>
        </TooltipWhite>
      </div>
    )}
  </div>
);

const IdeaInformation: React.FC<{
  ideaId: string;
  loading: boolean;
}> = ({ ideaId, loading }) => {
  const client = useApolloClient();

  const cachedIdea = client.readQuery<IdeaCoreQuery>({
    query: IdeaCore,
    variables: { ideaId }
  })?.idea;

  if (loading) return <IdeaInformationLoading />;

  const {
    catalystNames: catalysts,
    canCurrentUserViewIdeaFilterDetails: canViewIdeaFilterDetails,
    targetAllocation,
    timeframe,
    mentionedEntities,
    situation
  } = cachedIdea || {};

  const redacted = !canViewIdeaFilterDetails;

  return (
    <InfoTable title="Idea Information">
      {({ labelStyle, dataStyle }): React.ReactElement => (
        <table className={styles.ideaTable}>
          <tbody>
            <tr>
              <td className={labelStyle}>Situation</td>
              <td className={dataStyle}>{situation}</td>
            </tr>
            <tr>
              <td className={labelStyle}>Target Allocation</td>
              <td className={dataStyle}>{targetAllocation}</td>
            </tr>
            <tr>
              <td className={labelStyle}>Time Frame</td>
              <td className={classNames(dataStyle, { [styles.blur]: redacted })}>{timeframe}</td>
            </tr>
            <tr>
              <td className={labelStyle}>Catalysts</td>
              <td className={dataStyle}>{catalystItems(catalysts || [])}</td>
            </tr>
            {Boolean(mentionedEntities?.length) && (
              <tr>
                <td className={labelStyle}>Related Companies</td>
                <td className={dataStyle}>
                  {mentionedEntities
                    ?.reduce(
                      (acc: Maybe<MentionedEntity>[][], _currentVal, currentIdx, arr) =>
                        currentIdx % 2 === 0
                          ? [...acc, arr.slice(currentIdx, currentIdx + 2)]
                          : acc,
                      []
                    )
                    .map((innerArr, idx) => (
                      <div className={styles.tickerGroup} key={idx}>
                        {innerArr.map(
                          entity =>
                            entity?.primarySymbol && (
                              <span key={entity.id}>
                                <Link to={entityUrl(entity.id)}>{entity.primarySymbol}</Link>
                              </span>
                            )
                        )}
                      </div>
                    ))}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </InfoTable>
  );
};

export default IdeaInformation;
